/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import { PAMediaRemovalPayload } from "../mediaRemoval.types";
import { postAutoclaveServices } from "../../../../../service/postAutoclaveServices";
import { useState } from "react";

interface Props {
	selectedContainersId: string[];
	removalMethodId: string;
	remarks: string;
	mediaBatchId: string;
	mediaBatchName?: string;
	handleSuccess: () => void;
	showSuccessModal: () => void;
	toggleConfirmModal: () => void;
	showToast: (message: string, toastType: "success" | "error") => void;
}
const PAConfirmModal = ({
	selectedContainersId,
	removalMethodId,
	remarks,
	showSuccessModal,
	toggleConfirmModal,
	mediaBatchId,
	mediaBatchName,
	handleSuccess,
	showToast,
}: Props) => {
	const { t } = useTranslation();
	const [isPosting, setIsPosting] = useState(false);
	const handleConfirm = async () => {
		setIsPosting(true);
		const payload: PAMediaRemovalPayload = {
			media_batch_id: mediaBatchId,
			container_ids: selectedContainersId.join(","),
			removal_method_id: removalMethodId,
			remarks,
		};
		try {
			const success = await postAutoclaveServices.postMediaRemoval(payload);
			if (success) {
				toggleConfirmModal();
				showSuccessModal();
				handleSuccess();
			} else {
				toggleConfirmModal();
			}
		} catch (error: any) {
			showToast(
				error?.response?.data?.message || "An unexpected error occurred",
				"error",
			);
			toggleConfirmModal();
		} finally {
			setIsPosting(false);
		}
	};
	return (
		<div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
			<div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
				<div className="p-8 text-center">
					<div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-6">
						<span className="material-symbols-outlined text-[#FF7043] text-4xl">
							help
						</span>
					</div>
					<h3 className="text-xl font-black text-[#2D3E50] mb-2">
						{t("postAutoclaveProcess.mediaRemoval.confirmMediaRemoval")}
					</h3>
					<p className="text-sm text-slate-500 font-medium leading-relaxed">
						{t("postAutoclaveProcess.mediaRemoval.confirmationMessage")}{" "}
						<span className="font-black text-[#2D3E50]">
							{mediaBatchName}
						</span>
						{t("postAutoclaveProcess.mediaRemoval.complianceNotice")}
					</p>
				</div>
				<div className="bg-slate-50 p-6 flex gap-3">
					<button
						onClick={() => toggleConfirmModal()}
						className="flex-1 px-6 py-3 bg-white border border-slate-200 rounded-xl text-xs font-black text-slate-500 uppercase tracking-widest hover:bg-slate-100 transition-all"
					>
						{t("postAutoclaveProcess.mediaRemoval.noReview")}
					</button>
					<button
						onClick={handleConfirm}
						disabled={isPosting}
						className="flex-1 px-6 py-3 bg-[#FF7043] text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-orange-200 hover:bg-orange-600 transition-all disabled:opacity-50"
					>
						{t("postAutoclaveProcess.mediaRemoval.yesConfirm")}
					</button>
				</div>
			</div>
		</div>
	);
};

export default PAConfirmModal;
